import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { 
  FileText, Send, Clock, CheckCircle, AlertCircle, 
  X, ExternalLink, Loader2 
} from 'lucide-react';
import { showToast } from '../../utils/toast';
import { formatDateTimeEST } from '../../utils/timezone';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Valid contract status transitions
const VALID_TRANSITIONS = {
  "draft": ["sent", "cancelled"],
  "sent": ["viewed", "expired", "cancelled"],
  "viewed": ["signed_pending_payment", "expired", "cancelled"],
  "signed_pending_payment": ["active", "on_hold", "cancelled"],
  "active": ["on_hold", "cancelled"],
  "on_hold": ["active", "cancelled"],
  "expired": ["sent", "cancelled"],
  "cancelled": []
};

const STATUS_LABELS = {
  "draft": "Draft",
  "sent": "Sent",
  "viewed": "Viewed",
  "signed_pending_payment": "Signed – Pending Payment",
  "active": "Active",
  "on_hold": "On Hold",
  "cancelled": "Cancelled",
  "expired": "Expired"
};

export default function AdminContractSection({ clientId, currentStatus, onUpdate }) {
  const [contractStatus, setContractStatus] = useState(currentStatus || 'draft');
  const [contractInfo, setContractInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (clientId) {
      fetchContractInfo();
    }
  }, [clientId]);

  useEffect(() => {
    setContractStatus(currentStatus || 'draft');
  }, [currentStatus]);

  const fetchContractInfo = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/admin/contracts/${clientId}/status`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setContractInfo(data);
        setContractStatus(data.contract_status);
      }
    } catch (error) {
      console.error('Error fetching contract info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    const validTransitions = VALID_TRANSITIONS[contractStatus] || [];
    
    if (!validTransitions.includes(newStatus)) {
      showToast({
        title: 'Invalid Transition',
        message: `Cannot change from ${STATUS_LABELS[contractStatus]} to ${STATUS_LABELS[newStatus]}`,
        type: 'error'
      });
      return;
    }

    setUpdating(true);

    try {
      const response = await fetch(`${API}/api/admin/contracts/${clientId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        showToast({
          title: 'Status Updated',
          message: `Contract status changed to ${STATUS_LABELS[newStatus]}`,
          type: 'success'
        });
        setContractStatus(newStatus);
        await fetchContractInfo();
        if (onUpdate) onUpdate();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showToast({
        title: 'Update Failed',
        message: error.message || 'Failed to update contract status',
        type: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleSendContract = async () => {
    setSending(true);

    try {
      const response = await fetch(`${API}/api/admin/contracts/${clientId}/send`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        showToast({
          title: 'Contract Sent',
          message: 'Contract has been sent to client',
          type: 'success'
        });
        setContractStatus('sent');
        await fetchContractInfo();
        if (onUpdate) onUpdate();
      } else {
        throw new Error('Failed to send contract');
      }
    } catch (error) {
      console.error('Error sending contract:', error);
      showToast({
        title: 'Send Failed',
        message: 'Failed to send contract',
        type: 'error'
      });
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700 border-gray-300',
      sent: 'bg-blue-100 text-blue-700 border-blue-300',
      viewed: 'bg-purple-100 text-purple-700 border-purple-300',
      signed_pending_payment: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      active: 'bg-green-100 text-green-700 border-green-300',
      on_hold: 'bg-orange-100 text-orange-700 border-orange-300',
      cancelled: 'bg-red-100 text-red-700 border-red-300',
      expired: 'bg-gray-100 text-gray-700 border-gray-300'
    };

    const icons = {
      draft: <FileText className="w-3 h-3" />,
      sent: <Send className="w-3 h-3" />,
      viewed: <Clock className="w-3 h-3" />,
      signed_pending_payment: <AlertCircle className="w-3 h-3" />,
      active: <CheckCircle className="w-3 h-3" />,
      on_hold: <AlertCircle className="w-3 h-3" />,
      cancelled: <X className="w-3 h-3" />,
      expired: <Clock className="w-3 h-3" />
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.draft}`}>
        {icons[status] || icons.draft}
        {STATUS_LABELS[status] || status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return formatDateTimeEST(dateString);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  const validTransitions = VALID_TRANSITIONS[contractStatus] || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Contract Management
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Status */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm text-gray-600">Current Status</Label>
            {getStatusBadge(contractStatus)}
          </div>

          {/* Status Dropdown */}
          <div>
            <Label className="text-sm text-gray-600 mb-2 block">Change Status</Label>
            <Select 
              value={contractStatus}
              onValueChange={handleStatusChange}
              disabled={updating || validTransitions.length === 0}
            >
              <SelectTrigger>
                <SelectValue>
                  {STATUS_LABELS[contractStatus] || contractStatus}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={contractStatus} disabled>
                  {STATUS_LABELS[contractStatus]} (Current)
                </SelectItem>
                {validTransitions.map((status) => (
                  <SelectItem key={status} value={status}>
                    {STATUS_LABELS[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {validTransitions.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">
                No valid transitions from {STATUS_LABELS[contractStatus]}
              </p>
            )}
          </div>

          {/* Send Contract Button */}
          {contractStatus === 'draft' && (
            <Button
              onClick={handleSendContract}
              disabled={sending}
              size="sm"
              className="w-full"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Contract to Client
                </>
              )}
            </Button>
          )}
        </div>

        {/* Contract Info */}
        {contractInfo && (
          <div className="border-t pt-4">
            <h4 className="font-semibold text-sm text-gray-900 mb-3">Contract Details</h4>
            <div className="space-y-2 text-sm">
              {contractInfo.contract_sent_at && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Sent:</span>
                  <span className="text-gray-900">{formatDate(contractInfo.contract_sent_at)}</span>
                </div>
              )}
              {contractInfo.contract_signed_at && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Signed:</span>
                  <span className="text-gray-900">{formatDate(contractInfo.contract_signed_at)}</span>
                </div>
              )}
              {contractInfo.contract_updated_at && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Updated:</span>
                  <span className="text-gray-900">{formatDate(contractInfo.contract_updated_at)}</span>
                </div>
              )}
              {contractInfo.contract_document_url && (
                <div className="pt-2">
                  <a
                    href={contractInfo.contract_document_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 text-xs flex items-center gap-1"
                  >
                    <ExternalLink className="h-3 w-3" />
                    View Contract Document
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="border-t pt-4">
          <h4 className="font-semibold text-sm text-gray-900 mb-3">Activity Timeline</h4>
          <div className="space-y-3">
            {/* Draft */}
            <div className="flex items-start gap-3 text-sm">
              <div className="w-2 h-2 bg-gray-400 rounded-full mt-1.5"></div>
              <div className="flex-1">
                <p className="text-gray-700">Contract created</p>
                <p className="text-gray-500 text-xs">Status: Draft</p>
              </div>
            </div>

            {/* Sent */}
            {contractInfo?.contract_sent_at && (
              <div className="flex items-start gap-3 text-sm">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5"></div>
                <div className="flex-1">
                  <p className="text-gray-700">Contract sent to client</p>
                  <p className="text-gray-500 text-xs">{formatDate(contractInfo.contract_sent_at)}</p>
                </div>
              </div>
            )}

            {/* Signed */}
            {contractInfo?.contract_signed_at && (
              <div className="flex items-start gap-3 text-sm">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-1.5"></div>
                <div className="flex-1">
                  <p className="text-gray-700">Contract signed by client</p>
                  <p className="text-gray-500 text-xs">{formatDate(contractInfo.contract_signed_at)}</p>
                </div>
              </div>
            )}

            {/* Active */}
            {contractStatus === 'active' && (
              <div className="flex items-start gap-3 text-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5"></div>
                <div className="flex-1">
                  <p className="text-gray-700 font-medium">Contract activated</p>
                  <p className="text-gray-500 text-xs">Status: Active</p>
                </div>
              </div>
            )}

            {/* On Hold */}
            {contractStatus === 'on_hold' && (
              <div className="flex items-start gap-3 text-sm">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-1.5"></div>
                <div className="flex-1">
                  <p className="text-gray-700 font-medium">Contract on hold</p>
                  <p className="text-gray-500 text-xs">Payment or issue detected</p>
                </div>
              </div>
            )}

            {/* Cancelled/Expired */}
            {['cancelled', 'expired'].includes(contractStatus) && (
              <div className="flex items-start gap-3 text-sm">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5"></div>
                <div className="flex-1">
                  <p className="text-gray-700 font-medium">Contract {contractStatus}</p>
                  <p className="text-gray-500 text-xs">{formatDate(contractInfo?.contract_updated_at)}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
